import { formatarPercentual } from '@/design/formato';

/* plano-do-medidor.ts — onde cada texto do medidor de nível cabe.
 *
 * Puro, sem React e sem DOM: entra a fração cheia e os textos, sai a linha de
 * base de cada um, a TINTA de cada bloco e o tamanho que cada número precisa
 * ter para não atravessar a parede da célula. Mora fora do componente porque
 * é a parte que quebra, e quebrava em silêncio: enquanto era um par de
 * `Math.min` dentro do desenho, o medidor a 95% escrevia "o que falta" por
 * cima de "preço de hoje" e pintava o vermelho do campo escuro sobre o mel,
 * onde ele dá 1,3:1.
 *
 * Três regras, e as três vieram de um jeito de quebrar:
 *
 * 1. TINTA SEGUE O SUBSTRATO, não o significado. O mel é claro em qualquer
 *    tema (é a mesma cor no laudo claro e no veredito escuro), então texto
 *    sobre ele é sempre escuro; texto fora dele segue a escala de tinta, que
 *    se inverte sozinha dentro do campo mata. Bloco que muda de lado muda de
 *    tinta junto.
 * 2. BLOCO QUE NÃO CABE MUDA DE LADO, nunca é grampeado. Grampear duas
 *    âncoras contra o mesmo limite é o que empilhava os dois blocos nas
 *    pontas da escala, justamente onde o número mais importa.
 * 3. A PAREDE FECHA NAS PONTAS. A célula só tem a largura cheia no miolo; um
 *    número de sete dígitos escrito perto da ponta sai pela lateral e vai
 *    parar no fundo do painel, com a tinta do lado errado. Por isso o tamanho
 *    de cada linha é conferido contra a largura da célula NA ALTURA em que
 *    ela cai.
 *
 * Os dois blocos nunca faltam ao mesmo tempo: juntos medem ~117 de uma faixa
 * reta de 226, então sempre existe um lado inteiro para onde ir. O teste
 * varre a fração de 0 a 1 e cobra isso. */

/* ── A célula ─────────────────────────────────────────────────────────────
 * O recipiente é a célula da marca: ponta em cima, ponta embaixo, laterais
 * RETAS. Num hexágono regular um nível de 70% cairia na parte que estreita e
 * o menisco ficaria curto; aqui ele atravessa a largura inteira. */

export const CX = 150;
export const CY = 196;
export const LARGURA_CELULA = 176;
export const ALTURA_CELULA = 286;
/** Quanto de altura cada ponta consome antes de a parede ficar reta. */
export const PONTA = 50;

export const BORDA = CY - ALTURA_CELULA / 2;
export const BASE = CY + ALTURA_CELULA / 2;

/** Altura do desenho sem legenda quebrada. */
export const ALTURA_BASE = 384;

/* A faixa onde texto pode cair. Não é a célula inteira: 30 acima da ponta a
 * parede já abriu 105 de largura, que é o mínimo em que um valor em reais
 * ainda cabe sem encolher. */
const FAIXA_TOPO = BORDA + 30;
const FAIXA_BASE = BASE - 30;
/** Do menisco até o texto mais próximo, para os dois não se tocarem. */
const RESPIRO = 18;
/** Entre os dois blocos, quando dividem o mesmo lado. */
const ENTRE_BLOCOS = 16;

/* ── A escala de texto do medidor ─────────────────────────────────────────
 * Estes tamanhos são os mesmos que `.nivel-*` declarava em globals.css e
 * vieram para cá porque a conta de caber precisa deles em número. O CSS ficou
 * com o que é aparência (família, peso, trilha) e o componente passa o
 * `font-size`: duas fontes de verdade para o mesmo tamanho é como o desenho
 * volta a estourar sem ninguém ver. */

export type Papel = 'rotulo' | 'valor' | 'falta' | 'pct';

export const TAMANHO: Record<Papel, number> = {
  rotulo: 12,
  valor: 16,
  falta: 20,
  pct: 26,
};

/** Substrato sobre o qual um bloco de texto caiu. É ele que decide a tinta. */
export type Substrato = 'mel' | 'vazio';

export type LinhaDoMedidor = {
  readonly papel: Papel;
  readonly texto: string;
  readonly y: number;
  readonly tamanho: number;
};

export type BlocoDoMedidor = {
  readonly substrato: Substrato;
  readonly tinta: string;
  readonly linhas: readonly LinhaDoMedidor[];
};

export type EntradaDoMedidor = {
  readonly fracao: number;
  /** [rótulo, valor] do que falta encher. */
  readonly falta?: readonly [string, string] | undefined;
  /** [rótulo, valor] do que já está dentro. */
  readonly nivel?: readonly [string, string] | undefined;
  readonly legenda?: string | undefined;
};

export type PlanoDoMedidor = {
  /** A fração já grampeada em 0..1. */
  readonly cheia: number;
  /** Y do menisco: a superfície do mel. */
  readonly menisco: number;
  readonly falta: BlocoDoMedidor | null;
  readonly nivel: BlocoDoMedidor;
  readonly legenda: readonly string[];
  /** Altura do viewBox: cresce quando a legenda quebra em mais de uma linha. */
  readonly altura: number;
};

/* ── Largura disponível ───────────────────────────────────────────────── */

/** Quanto a célula mede de largura na altura `y`. Zero nas duas pontas. */
export function larguraNaAltura(y: number): number {
  const daPonta = Math.min(y - BORDA, BASE - y);
  return LARGURA_CELULA * Math.min(1, Math.max(0, daPonta / PONTA));
}

/* Avanço médio por caractere, em em. A JetBrains Mono avança 0,6 e a trilha
 * de cifra tira 0,03; o rótulo é da fonte de texto, mais estreita. Estimativa,
 * não medição: serve para decidir encolher, e errar para o lado seguro custa
 * meio ponto de tamanho, não um número cortado. */
const AVANCO: Record<Papel, number> = { rotulo: 0.55, valor: 0.57, falta: 0.57, pct: 0.56 };

/** O maior tamanho em que `texto` ainda cabe entre as paredes na altura `y`. */
function tamanhoQueCabe(papel: Papel, texto: string, y: number): number {
  const tamanho = TAMANHO[papel];
  // A parte alta do glifo é o que estreita quando a linha está acima do meio;
  // abaixo do meio quem chega primeiro na parede é a própria linha de base.
  const critico = y < CY ? y - 0.74 * tamanho : y;
  const disponivel = larguraNaAltura(critico) - 12;
  const largura = texto.length * AVANCO[papel] * tamanho;
  if (largura <= disponivel) return tamanho;
  // Piso: abaixo de 70% o número deixa de ser legível e é melhor deixar
  // encostar na parede do que fingir que coube.
  return Math.round(Math.max(tamanho * 0.7, (disponivel / largura) * tamanho) * 10) / 10;
}

/* ── Empilhamento ─────────────────────────────────────────────────────── */

type Pedaco = readonly [Papel, string];

/* Onde cai a linha de base de cada pedaço, medida do topo do bloco: altura de
 * caixa alta na primeira linha, e depois a descida da anterior mais a caixa
 * alta da seguinte. Sem isto o espaçamento entre um número de 26 e um rótulo
 * de 12 teria de ser adivinhado em número mágico. */
function deslocamentos(pedacos: readonly Pedaco[]): number[] {
  let y = 0;
  let anterior = 0;
  return pedacos.map(([papel], i) => {
    const t = TAMANHO[papel];
    y = i === 0 ? 0.74 * t : y + 0.3 * anterior + 0.74 * t + 4;
    anterior = t;
    return y;
  });
}

function alturaDoBloco(pedacos: readonly Pedaco[]): number {
  const dys = deslocamentos(pedacos);
  const ultimo = pedacos[pedacos.length - 1];
  if (!ultimo) return 0;
  return (dys[dys.length - 1] ?? 0) + 0.28 * TAMANHO[ultimo[0]];
}

const TINTA: Record<Substrato, { nivel: string; falta: string }> = {
  // Sobre o mel a tinta não pode se inverter com o tema: o mel é o mesmo
  // amarelo nos dois. Daí --mata e --negativo-mel, que são hex fixos.
  mel: { nivel: 'var(--mata)', falta: 'var(--negativo-mel)' },
  // Fora do mel vale a escala de tinta, que já se inverte sozinha dentro do
  // campo mata.
  vazio: { nivel: 'var(--tinta)', falta: 'var(--negativo)' },
};

type Caixa = { readonly topo: number; readonly substrato: Substrato; readonly tinta: string };

function montar(pedacos: readonly Pedaco[], caixa: Caixa): BlocoDoMedidor {
  const { topo, substrato, tinta } = caixa;
  const dys = deslocamentos(pedacos);
  return {
    substrato,
    tinta,
    linhas: pedacos.map(([papel, texto], i) => {
      const y = topo + (dys[i] ?? 0);
      return { papel, texto, y, tamanho: tamanhoQueCabe(papel, texto, y) };
    }),
  };
}

/* ── O plano ──────────────────────────────────────────────────────────── */

export function planejarMedidor(entrada: EntradaDoMedidor): PlanoDoMedidor {
  const { fracao, falta, nivel, legenda } = entrada;
  const cheia = Math.min(1, Math.max(0, Number.isFinite(fracao) ? fracao : 0));
  const menisco = BASE - cheia * ALTURA_CELULA;

  const pedacosFalta: Pedaco[] = falta ? [['rotulo', falta[0]], ['falta', falta[1]]] : [];
  const pedacosNivel: Pedaco[] = [
    ['pct', formatarPercentual(cheia)],
    ...(nivel ? ([['rotulo', nivel[0]], ['valor', nivel[1]]] as Pedaco[]) : []),
  ];

  const altFalta = falta ? alturaDoBloco(pedacosFalta) : 0;
  const altNivel = alturaDoBloco(pedacosNivel);

  // Quanto de faixa reta sobra de cada lado do menisco.
  const acima = menisco - RESPIRO - FAIXA_TOPO;
  const abaixo = FAIXA_BASE - RESPIRO - menisco;

  const nivelNoMel = abaixo >= altNivel;
  const faltaNoVazio = !nivelNoMel || acima >= altFalta;

  let topoFalta: number;
  let topoNivel: number;

  if (!nivelNoMel) {
    // Mel raso demais para escrever dentro dele: os dois blocos sobem para a
    // lâmina que falta, o de nível encostado no menisco, que é a linha que ele
    // nomeia.
    topoNivel = Math.min(menisco - RESPIRO - altNivel, FAIXA_BASE - altNivel);
    // O que falta continua pendurado no alto da lâmina, como no caso comum:
    // é ele que dá a medida do vazio, e vazio escrito no meio parece pouco.
    topoFalta = Math.max(FAIXA_TOPO, Math.min(BORDA + 34, topoNivel - ENTRE_BLOCOS - altFalta));
  } else if (!faltaNoVazio) {
    // Lâmina fina demais: os dois descem para o mel, o de falta encostado no
    // menisco, que é de onde a distância que ele mede começa.
    topoFalta = Math.max(menisco + RESPIRO, FAIXA_TOPO);
    topoNivel = topoFalta + (falta ? altFalta + ENTRE_BLOCOS : 0);
  } else {
    // O caso comum: o que falta na lâmina, o nível no mel.
    topoFalta = Math.min(BORDA + 34, menisco - RESPIRO - altFalta);
    topoNivel = menisco + RESPIRO;
  }

  // A tinta sai do MESMO booleano que decidiu o lado. Enquanto as duas coisas
  // eram decididas em lugares diferentes, o bloco mudava de lado e continuava
  // com a tinta do lado antigo — que é o salmão de 1,3:1 sobre o mel.
  const substratoNivel: Substrato = nivelNoMel ? 'mel' : 'vazio';
  const substratoFalta: Substrato = faltaNoVazio ? 'vazio' : 'mel';

  const linhasLegenda = legenda ? quebrarLegenda(legenda) : [];

  return {
    cheia,
    menisco,
    falta: falta
      ? montar(pedacosFalta, {
          topo: topoFalta,
          substrato: substratoFalta,
          tinta: TINTA[substratoFalta].falta,
        })
      : null,
    nivel: montar(pedacosNivel, {
      topo: topoNivel,
      substrato: substratoNivel,
      tinta: TINTA[substratoNivel].nivel,
    }),
    legenda: linhasLegenda,
    altura: ALTURA_BASE + Math.max(0, linhasLegenda.length - 1) * 18,
  };
}

/* A legenda é a única linha que usa a largura do desenho inteiro, e uma frase
 * com dois valores em reais passava dos 300 e era CORTADA pela viewport do
 * SVG — sem aviso, sem reticências. Quebrada, o desenho cresce em vez de
 * comer o texto. */
export function quebrarLegenda(texto: string, largura = 288): readonly string[] {
  const cabe = Math.max(8, Math.floor(largura / (TAMANHO.rotulo * AVANCO.rotulo)));
  const linhas: string[] = [];
  let atual = '';
  for (const palavra of texto.split(' ')) {
    const juntas = atual ? `${atual} ${palavra}` : palavra;
    if (juntas.length <= cabe || !atual) atual = juntas;
    else {
      linhas.push(atual);
      atual = palavra;
    }
  }
  if (atual) linhas.push(atual);
  return linhas;
}
